import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, divisionMeta, divisionOrder } from "@/lib/api";
import { Anchor, ArrowRight, Bus, GraduationCap, Users, Sunrise } from "lucide-react";

export default function Dashboard() {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    staleTime: 60_000,
  });
  const seasonYear = settings?.season_year || new Date().getFullYear();

  const { data: groups = [] } = useQuery({
    queryKey: ["groups"],
    queryFn: async () => (await api.get("/groups")).data,
  });
  const { data: buses = [] } = useQuery({
    queryKey: ["buses"],
    queryFn: async () => (await api.get("/buses")).data,
  });

  const totals = divisionOrder.reduce((acc, d) => {
    acc[d] = groups.filter((g) => g.division === d).length;
    return acc;
  }, {});
  const totalCounselors = groups.reduce(
    (n, g) => n + 1 + (g.assistants?.length || 0) + (g.cits?.length || 0),
    0
  );

  return (
    <div data-testid="dashboard-page">
      {/* Hero */}
      <section className="paper-card p-8 md:p-12 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#0B2545]/5 blur-2xl" />
        <div className="absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-[#E76F51]/8 blur-2xl" />

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="tag coral">Skippers</span>
          <span className="tag gold">Mariners</span>
          <span className="tag teal">Navigators</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] font-bold text-[#0B2545] max-w-3xl">
          Tip letters, ready in one click.
        </h1>
        <p className="mt-5 text-lg max-w-2xl text-[#13315C]/85 leading-relaxed">
          Generate printable tip letters for every group, bus, and CIT —
          populated from your staff placement, with grades mapped from HS to
          College automatically.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/groups"
            data-testid="hero-groups-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0B2545] text-[#FDF6EC] font-semibold hover:bg-[#13315C] transition-colors"
          >
            Browse Group Letters <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/buses"
            data-testid="hero-buses-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#0B2545] font-semibold border border-[#0B2545] hover:bg-[#0B2545] hover:text-[#FDF6EC] transition-colors"
          >
            Bus Letters (1–33)
          </Link>
        </div>

        {/* Stat strip */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Groups" value={groups.length} icon={Users} />
          <Stat label="Buses" value={buses.length} icon={Bus} />
          <Stat label="Counselors" value={totalCounselors} icon={GraduationCap} />
          <Stat label="Divisions" value={3} icon={Anchor} />
        </div>
      </section>

      {/* Division breakdown */}
      <section className="mt-10">
        <SectionHeading eyebrow="Divisions" title="Choose your division" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
          {divisionOrder.map((d) => {
            const meta = divisionMeta[d];
            const list = groups.filter((g) => g.division === d);
            return (
              <Link
                key={d}
                to={`/groups?division=${d}`}
                data-testid={`division-card-${d.toLowerCase()}`}
                className="tile paper-card p-6 flex flex-col justify-between min-h-[220px]"
              >
                <div>
                  <span className={`tag ${meta.accent}`}>{meta.range}</span>
                  <h3 className="font-display text-3xl font-bold text-[#0B2545] mt-4">
                    {meta.label}
                  </h3>
                  <p className="text-sm text-[#13315C]/70 mt-1">{meta.subtitle}</p>
                </div>
                <div className="mt-6 flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-display font-bold text-[#0B2545]">
                      {totals[d] || 0}
                    </div>
                    <div className="text-xs uppercase tracking-widest text-[#13315C]/60 font-mono-camp">
                      Groups
                    </div>
                  </div>
                  <span className="inline-flex items-center text-sm font-semibold text-[#0B2545] group">
                    Open <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {list.slice(0, 8).map((g) => (
                    <span key={g.code} className="text-[10px] font-mono-camp px-2 py-0.5 rounded bg-[#0B2545]/6 text-[#0B2545]/70">
                      {g.code}
                    </span>
                  ))}
                  {list.length > 8 && (
                    <span className="text-[10px] font-mono-camp px-2 py-0.5 rounded bg-[#0B2545]/6 text-[#0B2545]/70">
                      +{list.length - 8}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Quick tiles */}
      <section className="mt-12">
        <SectionHeading eyebrow="Other letters" title="Bus · CIT · AM/PM" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
          <QuickTile
            to="/buses"
            icon={Bus}
            title="Bus Letters"
            desc="33 bus routes. Same format as group letters. Add driver + counselor per bus."
            testid="quick-buses"
            accent="gold"
          />
          <QuickTile
            to="/cit"
            icon={GraduationCap}
            title="CIT Supervisors"
            desc="Dedicated CIT tip policy. Each group letter also lists its own CIT."
            testid="quick-cit"
            accent="teal"
          />
          <QuickTile
            to="/ampm"
            icon={Sunrise}
            title="AM & PM Care"
            desc="Before-camp and after-camp counselors. One shared tip letter for AM + PM staff."
            testid="quick-ampm"
            accent="coral"
          />
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="paper-tint p-4 flex items-center gap-3">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#0B2545] text-[#FDF6EC]">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <div className="text-2xl font-display font-bold text-[#0B2545] leading-none">
          {value}
        </div>
        <div className="text-[11px] uppercase tracking-widest text-[#13315C]/60 font-mono-camp mt-1">
          {label}
        </div>
      </div>
    </div>
  );
}

function QuickTile({ to, icon: Icon, title, desc, testid, accent }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="tile paper-card p-6 flex flex-col justify-between min-h-[180px]"
    >
      <div className="flex items-start justify-between">
        <span className={`tag ${accent}`}>Tip Letter</span>
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#0B2545]/6 text-[#0B2545]">
          <Icon className="w-5 h-5" />
        </span>
      </div>
      <div className="mt-4">
        <h4 className="font-display text-2xl font-bold text-[#0B2545]">{title}</h4>
        <p className="text-sm text-[#13315C]/75 mt-1">{desc}</p>
      </div>
      <div className="mt-3 text-sm font-semibold text-[#0B2545] inline-flex items-center">
        Open <ArrowRight className="w-4 h-4 ml-1" />
      </div>
    </Link>
  );
}

function SectionHeading({ eyebrow, title }) {
  return (
    <div>
      <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
        {eyebrow}
      </div>
      <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0B2545] mt-1">
        {title}
      </h2>
      <div className="mt-3 wave-line">~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~</div>
    </div>
  );
}
